import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AppState } from 'react-native';

import { apiFetch } from '../api/apiService';
import { API_ENDPOINTS } from '../api/config';
import type { PaywallStatusResponse } from '../types/paywall';

export interface UsePaywallStatusResult {
  status: PaywallStatusResponse | null;
  isLoading: boolean;
  /**
   * Tri-state hydration flag. `true` while the entitlement is still unknown
   * (status === null and the request has not resolved yet). Consumers must NOT
   * treat an unknown entitlement as "no entitlement" — that was the root cause
   * of the self-pay dashboard redirecting back to /onboarding (jobs 884/887).
   */
  isUnknown: boolean;
  refetch: () => Promise<void>;
  /**
   * Explicit, user-initiated restart (#402 B3). `refetch` is intentionally a
   * no-op once the per-generation request budget is spent (review !383
   * security finding against retry amplification), so the fail-closed screen's
   * `Tentar novamente` action uses `retry` instead: it opens a FRESH generation
   * with its own bounded budget, letting a driver recover a transient network
   * failure without a full reload.
   */
  retry: () => void;
}

export interface UsePaywallStatusOptions {
  /**
   * When false, the hook stays idle (no request, no listener) and reports
   * isLoading=false. Lets a screen mount the hook unconditionally — hooks
   * cannot be called in a branch — and still avoid a status call it does not
   * need, e.g. a driver who already has an active employment (WS7/#207).
   */
  enabled?: boolean;
}

/**
 * Hard cap on the number of requests issued PER GENERATION (review !383
 * security finding). A driver that starts UNKNOWN is blocked from the dashboard
 * until the entitlement resolves, so recovery cannot depend solely on AppState
 * returning to 'active' — on web that event may never fire in a single page
 * lifetime. These retries are issued back-to-back inside a SINGLE in-flight
 * runFetch: NO timer, NO arbitrary sleep, and they stop as soon as a conclusive
 * status arrives or the budget is spent (after which UNKNOWN persists and
 * access stays blocked — never granted).
 *
 * Single-flight (review !383 security finding): mount + repeated AppState
 * 'active' + multiple concurrent refetch calls all share ONE in-flight runFetch
 * for the current generation. While a run is active, further triggers are a
 * no-op; once it settles the per-generation budget (this constant) is the
 * absolute max for the whole generation — never 3×N.
 *
 * Per-generation budget (review !383 FINAL blocker): the request count lives in
 * a bucket KEYED BY the captured generation number, NOT a single shared
 * counter. A stale response from a superseded generation must therefore NEVER
 * increment/consume the CURRENT generation's budget. The attempt is reserved in
 * the capturing generation's bucket BEFORE apiFetch fires (and only after
 * confirming isCurrent), so a late completion of an old request cannot touch
 * the new generation's count or write its status.
 */
const GENERATION_MAX_REQUESTS = 3;

export const PaywallStatusContext = createContext<UsePaywallStatusResult | null>(null);

export const usePaywallStatusContext = (): UsePaywallStatusResult | null =>
  useContext(PaywallStatusContext);

/**
 * Paywall state machine client (#209): fetches /api/subscriptions/status/ on
 * mount and again whenever the app returns to the foreground — the D-0 block
 * must kick in when the user reopens the app after the trial ended, not only
 * on a fresh login. Errors keep the last known status (never lock the user
 * out because of a network blip).
 */
export const usePaywallStatus = (
  { enabled = true }: UsePaywallStatusOptions = {},
): UsePaywallStatusResult => {
  const [status, setStatus] = useState<PaywallStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  // Tri-state hydration: entitlement is UNKNOWN until the first status resolves
  // (or we learn there is none). While unknown, consumers must not treat the
  // driver as "no entitlement" — that caused the self-pay dashboard to bounce to
  // /onboarding (jobs 884/887).
  //
  // #379: this flag must be UNKNOWN **during the very render in which `enabled`
  // flips false→true, not only after the effect runs. A state-only flag was
  // still `false` on that commit, so a consumer effect declared after this hook
  // read `isUnknown=false` + `status=null` and derived a CONCLUSIVE "no
  // entitlement", bouncing an entitled self-pay driver to /onboarding on the
  // second reload (job 15917386343). `unknownFromError` only ADDS unknown-ness
  // for a failed request that never produced a conclusive status.
  const [unknownFromError, setUnknownFromError] = useState<boolean>(false);
  // Sticky: once a conclusive status has been observed, the entitlement is known
  // for the rest of this hook's life. A later HTTP/network failure must NOT
  // invalidate a conclusive status we already have (that would re-open the
  // hydration hole and bounce an entitled self-pay driver to /onboarding).
  const hasResolvedRef = useRef<boolean>(false);

  /**
   * Generation fence (review !383 finding 1). A shared `mounted` boolean cannot
   * distinguish "this response belongs to the current enabled window" from "this
   * response belongs to a window that was already torn down and re-created".
   * With enabled true→false→true, request A issued in the first window would
   * still see `mounted.current === true` (the new effect had set it back to
   * true) and could write a stale status / release the entitlement.
   *
   * Every enabled window bumps the generation; a response is only allowed to
   * touch state while its captured generation is still the current one.
   */
  const generationRef = useRef(0);
  const isCurrent = useCallback((generation: number) => generationRef.current === generation, []);

  /**
   * Single-flight state for the current generation:
   *  - `inflightPromiseRef`: the one runFetch promise shared by every trigger
   *    (mount / AppState 'active' / refetch) while it is in flight.
   *  - `generationCountsRef`: a Map<generation, requestCount>. The budget is
   *    PER GENERATION — a stale response from a superseded generation lands in
   *    its OWN bucket and can never alter the current generation's count.
   */
  const inflightPromiseRef = useRef<Promise<void> | null>(null);
  const generationCountsRef = useRef<Map<number, number>>(new Map());

  const runFetch = useCallback(async (generation: number): Promise<void> => {
    // Retry loop, bounded by THIS generation's own request budget.
    while (true) {
      // Abandon the moment this generation is superseded (enabled flipped, or
      // the hook unmounted): never touch the current generation's budget or
      // state from a stale window. This guard runs BEFORE reserving a slot.
      if (!isCurrent(generation)) return;

      // Reserve this generation's attempt BEFORE firing the request. Because the
      // count is keyed by the captured `generation`, an old pending request
      // (whose own captured generation is no longer current) either hits this
      // isCurrent guard and returns, or reserves in its OWN stale bucket — it
      // can never consume the CURRENT generation's budget (review !383 final
      // blocker).
      const used = generationCountsRef.current.get(generation) ?? 0;
      if (used >= GENERATION_MAX_REQUESTS) {
        // This generation has spent its own budget. Settle loading state and
        // stop — fail-closed, still UNKNOWN, access never granted.
        if (isCurrent(generation)) setIsLoading(false);
        return;
      }
      generationCountsRef.current.set(generation, used + 1);

      try {
        const response = await apiFetch<PaywallStatusResponse>(
          API_ENDPOINTS.SUBSCRIPTION_STATUS,
          { method: 'GET' },
        );
        // Stale completion: discard, never write state or touch any budget.
        if (!isCurrent(generation)) return;

        if (response.ok && response.data?.state) {
          setStatus(response.data);
          hasResolvedRef.current = true;
          setUnknownFromError(false);
          setIsLoading(false);
          return;
        }

        if (!hasResolvedRef.current) setUnknownFromError(true);
      } catch {
        // Stale completion: discard, never write state or touch any budget.
        if (!isCurrent(generation)) return;
        if (!hasResolvedRef.current) setUnknownFromError(true);
      }

      // Only keep retrying while the entitlement has NEVER been conclusive —
      // that is the state which blocks the driver. Once we hold a conclusive
      // status a transient failure is harmless and must not spin.
      if (hasResolvedRef.current) break;
    }

    if (isCurrent(generation)) setIsLoading(false);
  }, [isCurrent]);

  /**
   * Single-flight entry point (review !383 security finding). Every trigger —
   * mount, repeated AppState 'active', concurrent refetch calls — converges
   * here. While a runFetch is active for the current generation it returns the
   * shared promise (dedup / no-op); once the budget is spent it returns a
   * resolved promise without issuing a new request (fail-closed).
   */
  const ensureFetch = useCallback((generation: number): Promise<void> => {
    if (generationRef.current !== generation) return Promise.resolve();
    if (inflightPromiseRef.current) return inflightPromiseRef.current;
    if ((generationCountsRef.current.get(generation) ?? 0) >= GENERATION_MAX_REQUESTS) {
      return Promise.resolve();
    }

    const promise = runFetch(generation);
    inflightPromiseRef.current = promise;
    // Release the single-flight slot once this run settles, unless a newer
    // generation already started its own run.
    void promise.finally(() => {
      if (inflightPromiseRef.current === promise) inflightPromiseRef.current = null;
    });
    return promise;
  }, [runFetch]);

  // Derived (never lags a render): unknown while enabled and no conclusive
  // status has been observed yet.
  const isUnknown = enabled ? status === null || unknownFromError : false;

  const refetch = useCallback((): Promise<void> => {
    if (!enabled) return Promise.resolve();
    return ensureFetch(generationRef.current);
  }, [enabled, ensureFetch]);

  /**
   * Explicit user-initiated restart (#402 B3): bump a nonce that the enabled
   * window effect depends on, so a fresh generation (new budget, re-bound
   * AppState listener) starts exactly like a mount or an enabled toggle. Only
   * the deliberate retry control uses this; automatic triggers keep sharing the
   * per-generation budget.
   */
  const [retryNonce, setRetryNonce] = useState(0);
  const retry = useCallback((): void => {
    setRetryNonce(nonce => nonce + 1);
  }, []);

  useEffect(() => {
    // New window (mount, or enabled flipping): invalidate every in-flight
    // response from the previous one and reset THIS generation's budget bucket.
    generationRef.current += 1;
    const generation = generationRef.current;
    generationCountsRef.current = new Map();
    inflightPromiseRef.current = null;

    if (!enabled) {
      setIsLoading(false);
      setUnknownFromError(false);
      return () => {
        // Teardown also invalidates: a late response must not resurrect state.
        generationRef.current += 1;
        inflightPromiseRef.current = null;
      };
    }

    setIsLoading(true);
    ensureFetch(generation);
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') ensureFetch(generation);
    });
    return () => {
      generationRef.current += 1;
      inflightPromiseRef.current = null;
      subscription.remove();
    };
  }, [enabled, ensureFetch, retryNonce]);

  return { status, isLoading, isUnknown, refetch, retry };
};

export const PaywallStatusProvider = ({ children }: { children: ReactNode }) => {
  const value = usePaywallStatus();
  return createElement(PaywallStatusContext.Provider, { value }, children);
};
